import { SheetWithFormComponent } from '@/components/ui/SheetWithFormComponent'
import { useRestClient } from '@/contexts/QueryProvider'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { useCurrentOrganisation } from '../useCurrentOrganisation'
import { LinkedApplicationsField } from './components/LinkedApplicationsField'
import { UserOrNomineeListField } from './components/RequestFormFields'
import { Button } from '@/components/shadcn/button'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/shadcn/form'
import { Input } from '@/components/shadcn/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcn/select'
import { applicationAssignmentSchema, userOrNomineeSchema } from '../Requests/schemas'
import {
  ApplicationAccessBlock,
  isBlankPerson,
  isCompletePerson,
  PROJECT_FREQUENCY_OPTIONS,
  ProjectCreateTicket,
  uniquePeople,
  UserOrNominee,
} from '../Requests/types'

const formSchema = z.object({
  projectName: z.string().min(1, 'Project name is required'),
  frequency: z.enum(['Daily', 'Weekly', 'Monthly', 'Annually', 'Ad hoc']),
  projectLeaders: z
    .array(userOrNomineeSchema)
    .min(1, 'At least one project leader is required')
    .refine((people) => people.every(isCompletePerson), {
      message: 'Each project leader must be a selected user or a complete nominee',
    }),
  projectAdministrators: z
    .array(userOrNomineeSchema)
    .optional()
    .refine((people) => !people?.length || people.every((person) => isCompletePerson(person) || isBlankPerson(person)), {
      message: 'Finish or remove incomplete administrators',
    }),
  projectMembers: z
    .array(userOrNomineeSchema)
    .optional()
    .refine((people) => !people?.length || people.every((person) => isCompletePerson(person) || isBlankPerson(person)), {
      message: 'Finish or remove incomplete members',
    }),
  applications: z
    .array(
      z.object({
        applicationId: z.number(),
        assignments: z.array(applicationAssignmentSchema),
      })
    )
    .min(1, 'Select at least one linked application')
    .superRefine((apps, ctx) => {
      apps.forEach((app, index) => {
        if (!app.assignments.some((assignment) => assignment.roleIds.length > 0)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Assign at least one role to a nominated person',
            path: [index, 'assignments'],
          })
        }
      })
    }),
})

type FormValues = z.infer<typeof formSchema>

type RequestProjectCreateFormProps = {
  onSubmit?: () => void
  sheetTrigger?: React.ReactNode
  sheetTriggerTooltip?: string
  sheetTitle?: string
  sheetDescription?: string
  organisationId?: number
}

export function RequestProjectCreateForm(props: RequestProjectCreateFormProps) {
  const [sheetOpen, setSheetOpen] = useState(false)
  const onSubmit = useCallback(() => {
    props.onSubmit?.()
    setSheetOpen(false)
  }, [props])

  return props.sheetTrigger ? (
    <SheetWithFormComponent
      title={props.sheetTitle ?? 'Request project'}
      description={props.sheetDescription}
      wide
      isOpen={sheetOpen}
      onOpenChange={setSheetOpen}
      trigger={props.sheetTrigger}
      triggerTooltip={props.sheetTriggerTooltip}
    >
      <FormComponent {...props} onSubmit={onSubmit} />
    </SheetWithFormComponent>
  ) : (
    <FormComponent {...props} onSubmit={onSubmit} />
  )
}

function FormComponent({
  onSubmit,
  organisationId,
}: RequestProjectCreateFormProps & { onSubmit: () => void }) {
  const { id: orgId } = useCurrentOrganisation(organisationId)
  const {
    getSubscriptionsApiListOrganisationUsers,
    getApplicationsApiGetApplications,
    putRequestsApiAddRequest,
    getRequestsApiGetOrganisationRequests,
  } = useRestClient()

  const { data: users } = getSubscriptionsApiListOrganisationUsers.useQuery(
    { query: { orgId } },
    { enabled: !!orgId }
  )
  const { data: applications } = getApplicationsApiGetApplications.useQuery(undefined)

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      projectName: '',
      frequency: 'Monthly',
      projectLeaders: [],
      projectAdministrators: [],
      projectMembers: [],
      applications: [],
    },
  })

  const userOptions = useMemo(
    () =>
      users?.map((u) => ({
        value: u.id!,
        label: `${u.givenName} ${u.familyName}`,
        description: u.email,
      })) ?? [],
    [users]
  )

  const applicationOptions = useMemo(
    () =>
      applications?.map((a) => ({
        value: a.applicationId!,
        label: a.appName ?? `App ${a.applicationId}`,
      })) ?? [],
    [applications]
  )

  const { mutate, isPending } = putRequestsApiAddRequest.useMutation(undefined, {
    onSuccess: () => {
      getRequestsApiGetOrganisationRequests.invalidateQueries({ parameters: { query: { orgId } } })
      toast.success('Project create request submitted')
      onSubmit()
      form.reset()
    },
    onError: () => toast.error('Failed to submit project create request'),
  })

  const projectLeaders = (form.watch('projectLeaders') ?? []) as UserOrNominee[]
  const projectAdministrators = (form.watch('projectAdministrators') ?? []) as UserOrNominee[]
  const projectMembers = (form.watch('projectMembers') ?? []) as UserOrNominee[]
  const nominatedPeople = uniquePeople([...projectLeaders, ...projectAdministrators, ...projectMembers])

  const handleSubmit = form.handleSubmit(
    (values) => {
      if (!orgId) {
        toast.error('No organisation ID found')
        return
      }

      const ticket: ProjectCreateTicket = {
        projectName: values.projectName,
        frequency: values.frequency,
        projectLeaders: values.projectLeaders as UserOrNominee[],
        projectAdministrators: (values.projectAdministrators as UserOrNominee[] | undefined)?.filter(isCompletePerson),
        projectMembers: (values.projectMembers as UserOrNominee[] | undefined)?.filter(isCompletePerson),
        applications: (values.applications as ApplicationAccessBlock[]).map((block) => ({
          ...block,
          assignments: block.assignments.filter((assignment) => assignment.roleIds.length > 0),
        })),
      }
      mutate({
        body: {
          ticketType: 2,
          organisationId: orgId,
          ticket: JSON.stringify(ticket),
        },
      })
    },
    () => {
      toast.error('Please complete project leaders, linked applications, and at least one application role')
    }
  )

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit} className="space-y-6">
        <FormField
          control={form.control}
          name="projectName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Project name *</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="frequency"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Frequency *</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select frequency" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {PROJECT_FREQUENCY_OPTIONS.map((freq) => (
                    <SelectItem key={freq} value={freq}>
                      {freq}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        <UserOrNomineeListField
          label="Project leaders"
          required
          userOptions={userOptions}
          value={projectLeaders}
          onChange={(next) => form.setValue('projectLeaders', next, { shouldValidate: true })}
        />
        <UserOrNomineeListField
          label="Project administrators"
          userOptions={userOptions}
          value={projectAdministrators}
          onChange={(next) => form.setValue('projectAdministrators', next, { shouldValidate: true })}
        />
        <UserOrNomineeListField
          label="Project members"
          userOptions={userOptions}
          value={projectMembers}
          onChange={(next) => form.setValue('projectMembers', next, { shouldValidate: true })}
        />
        <LinkedApplicationsField
          applicationOptions={applicationOptions}
          userOptions={userOptions}
          assignmentPeople={nominatedPeople}
          value={form.watch('applications') as ApplicationAccessBlock[]}
          onChange={(applications) => form.setValue('applications', applications, { shouldValidate: true })}
        />
        <Button type="submit" disabled={isPending}>
          Request project
        </Button>
      </form>
    </Form>
  )
}
